import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Mail, TrendingUp, MousePointerClick, MailOpen, MailX, UserMinus, AlertTriangle, BarChart3, Copy, Check, Eye, X as XIcon, HeartCrack } from "lucide-react";
import { BRANCH_META } from "@/lib/branches";

/**
 * Admin-only email analytics dashboard.
 * Reads /api/admin/emails/analytics — needs an admin token.
 * We DON'T force the user to paste the token here — instead we require
 * `role === "admin"` on the current user AND look up the token from
 * localStorage (settable via the "paste admin token" input, one time).
 */
const TOKEN_KEY = "atlas-admin-api-token";

export default function AdminEmails() {
    const { user } = useAuth();
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [tokenDraft, setTokenDraft] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [days, setDays] = useState(28);
    const [copied, setCopied] = useState(false);
    // Digest preview modal
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    const [previewEmail, setPreviewEmail] = useState("");
    // Cancel-reason survey aggregate
    const [reasons, setReasons] = useState(null);

    const isAdmin = user && (user.role === "admin" || user.email === "admin@atlas.app" || user.email === "purposelyy@gmail.com");

    const load = async (tok = token, d = days) => {
        if (!tok) return;
        setLoading(true);
        setError(null);
        try {
            const [analyticsRes, reasonsRes] = await Promise.all([
                api.get(`/admin/emails/analytics?days=${d}`, { headers: { "X-Admin-Token": tok } }),
                api.get(`/admin/cancel-reasons?days=${d}`, { headers: { "X-Admin-Token": tok } })
                    .catch(() => ({ data: null })),
            ]);
            setData(analyticsRes.data);
            setReasons(reasonsRes.data);
        } catch (e) {
            setError(e.response?.data?.detail || "Analytics failed to load — token invalid?");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const openPreview = async () => {
        setPreviewOpen(true);
        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewData(null);
        try {
            const q = previewEmail.trim() ? `?email=${encodeURIComponent(previewEmail.trim())}` : "";
            const { data } = await api.get(`/admin/digest/preview${q}`, {
                headers: { "X-Admin-Token": token },
            });
            setPreviewData(data);
        } catch (e) {
            setPreviewError(e.response?.data?.detail || "Could not render the digest preview");
        } finally {
            setPreviewLoading(false);
        }
    };

    useEffect(() => {
        if (token) load(token, days);
    }, [days]);

    const saveToken = () => {
        const t = tokenDraft.trim();
        if (!t) return;
        localStorage.setItem(TOKEN_KEY, t);
        setToken(t);
        setTokenDraft("");
        load(t, days);
    };

    if (user && !isAdmin) return <Navigate to="/atlas" replace />;
    if (!user) return null;

    const t = data?.totals || {};
    const weekly = data?.weekly || {};
    const weeks = Object.keys(weekly).sort();

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-5xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <p className="chip" style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }}>
                    <BarChart3 className="w-3 h-3" /> Admin · Email Analytics
                </p>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-2 leading-none">
                    Weekly digest performance.
                </h1>
                <p className="text-slate-600 mt-2 max-w-2xl">
                    Real send + open + click + bounce counts pulled from Resend webhooks plus our own send log. Time window is configurable.
                </p>

                {!token && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="crystal rounded-3xl p-6 mt-6" data-testid="admin-token-gate">
                        <div className="flex items-center gap-2 text-amber-800">
                            <AlertTriangle className="w-4 h-4" />
                            <div className="font-display font-bold">Paste your admin API token to load the dashboard</div>
                        </div>
                        <p className="text-sm text-slate-600 mt-2">
                            One-time. Stored in your browser's local storage (never sent anywhere except <code>/api/admin/*</code>). Value comes from the <code>ADMIN_API_TOKEN</code> in <code>/app/backend/.env</code>.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <input
                                type="password"
                                value={tokenDraft}
                                onChange={(e) => setTokenDraft(e.target.value)}
                                placeholder="ip_yonrmXFEm8h..."
                                className="field-input flex-1 min-w-[240px] font-mono text-sm"
                                data-testid="admin-token-input"
                            />
                            <button onClick={saveToken} className="arcade-btn" data-variant="primary" data-testid="admin-token-save">
                                Save
                            </button>
                        </div>
                    </motion.div>
                )}

                {token && (
                    <>
                        <div className="mt-6 flex flex-wrap gap-1.5 items-center">
                            {[7, 14, 28, 60, 90].map((d) => (
                                <button
                                    key={d}
                                    onClick={() => setDays(d)}
                                    data-testid={`admin-window-${d}`}
                                    className={`chip transition-all ${days === d ? "bg-slate-900 text-cream" : "bg-white/70 text-slate-700 hover:bg-white"}`}
                                >
                                    Last {d} days
                                </button>
                            ))}
                            <input
                                type="email"
                                value={previewEmail}
                                onChange={(e) => setPreviewEmail(e.target.value)}
                                placeholder="preview as user@… (optional)"
                                className="field-input h-9 text-xs px-3 max-w-[220px]"
                                data-testid="admin-preview-email-input"
                            />
                            <button
                                onClick={openPreview}
                                className="arcade-btn text-xs h-9"
                                data-variant="primary"
                                data-testid="admin-preview-digest"
                            >
                                <Eye className="w-3 h-3" /> Preview digest
                            </button>
                            <button
                                onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setData(null); }}
                                className="text-xs text-slate-500 hover:text-slate-900 font-display font-bold ml-auto"
                                data-testid="admin-token-clear"
                            >
                                Clear admin token
                            </button>
                        </div>

                        {loading && <p className="mt-4 text-slate-500 text-sm">Crunching stats…</p>}
                        {error && (
                            <div className="crystal rounded-2xl p-4 mt-4 text-white bg-sciences" data-testid="admin-analytics-error">
                                {error}
                            </div>
                        )}

                        {data && (
                            <>
                                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-analytics-cards">
                                    <Stat label="Sent" value={t.sent} color="#6F00FF" Icon={Mail} />
                                    <Stat label="Delivered" value={t.delivered} color="#3EB489" Icon={MailOpen} />
                                    <Stat label="Opened" value={t.opened} rate={t.open_rate} color="#FFC107" Icon={TrendingUp} />
                                    <Stat label="Clicked" value={t.clicked} rate={t.click_rate} color="#1E88E5" Icon={MousePointerClick} />
                                    <Stat label="Bounced" value={t.bounced} color="#FF2052" Icon={MailX} />
                                    <Stat label="Complaints" value={t.complained} color="#FF2052" Icon={AlertTriangle} />
                                    <Stat label="Unsubscribed" value={t.unsubscribed} color="#94A3B8" Icon={UserMinus} />
                                    <Stat label="Open rate" value={`${((t.open_rate || 0) * 100).toFixed(1)}%`} color="#FFC107" Icon={TrendingUp} />
                                </div>

                                {/* Weekly bars */}
                                <div className="mt-8 crystal rounded-3xl p-6" data-testid="admin-weekly-bars">
                                    <div className="flex items-center justify-between">
                                        <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                                            Weekly volume
                                        </h2>
                                        <span className="text-xs text-slate-500">{weeks.length} weeks · legend: sent · opened · clicked</span>
                                    </div>
                                    {weeks.length === 0 && (
                                        <p className="mt-3 text-sm text-slate-500">No events in this window yet. Send a test digest from Settings.</p>
                                    )}
                                    <div className="mt-3 space-y-1.5">
                                        {weeks.map((wk) => {
                                            const b = weekly[wk] || {};
                                            const s = (b["email.sent"] || 0) + (b["email.delivered"] || 0);
                                            const o = b["email.opened"] || 0;
                                            const c = b["email.clicked"] || 0;
                                            const maxRow = Math.max(1, s, o, c);
                                            return (
                                                <div key={wk} className="grid grid-cols-[auto,1fr] gap-3 items-center" data-testid={`admin-week-${wk}`}>
                                                    <div className="text-[11px] font-mono text-slate-500 w-16 text-right">{wk}</div>
                                                    <div className="flex items-center gap-2">
                                                        <Bar value={s} max={maxRow} color="#6F00FF" label="sent" />
                                                        <Bar value={o} max={maxRow} color="#FFC107" label="opened" />
                                                        <Bar value={c} max={maxRow} color="#1E88E5" label="clicked" />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Top URLs */}
                                {data.top_clicked_urls?.length > 0 && (
                                    <div className="mt-6 crystal rounded-3xl p-6" data-testid="admin-top-urls">
                                        <h2 className="font-display font-bold text-xl tracking-tight text-slate-900 flex items-center gap-2">
                                            <MousePointerClick className="w-4 h-4" color={BRANCH_META.sciences.color} /> Top-clicked URLs
                                        </h2>
                                        <div className="mt-3 space-y-2">
                                            {data.top_clicked_urls.map((u, i) => (
                                                <div key={i} className="flex items-center justify-between gap-3 text-sm">
                                                    <a href={u.url} target="_blank" rel="noreferrer" className="truncate text-humanities font-mono text-xs hover:underline">
                                                        {u.url}
                                                    </a>
                                                    <span className="font-display font-black text-slate-900">{u.clicks}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Bounces */}
                                {data.top_bounced?.length > 0 && (
                                    <div className="mt-6 crystal rounded-3xl p-6" data-testid="admin-top-bounces">
                                        <h2 className="font-display font-bold text-xl tracking-tight text-slate-900 flex items-center gap-2">
                                            <MailX className="w-4 h-4" color={BRANCH_META.arts.color} /> Bouncing addresses
                                        </h2>
                                        <p className="text-xs text-slate-500 mt-1">These are auto-unsubscribed to protect sender reputation.</p>
                                        <div className="mt-3 space-y-2">
                                            {data.top_bounced.map((b, i) => (
                                                <div key={i} className="flex items-center justify-between gap-3 text-sm">
                                                    <span className="truncate font-mono text-xs text-slate-700">{b.email}</span>
                                                    <span className="font-display font-black text-slate-900">{b.bounces}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Cancel-reason survey aggregate — churn diagnostic. */}
                                {reasons && (
                                    <div className="mt-6 crystal rounded-3xl p-6" data-testid="admin-cancel-reasons">
                                        <h2 className="font-display font-bold text-xl tracking-tight text-slate-900 flex items-center gap-2">
                                            <HeartCrack className="w-4 h-4" color={BRANCH_META.arts.color} /> Why people cancel
                                        </h2>
                                        <p className="text-xs text-slate-500 mt-1">
                                            {reasons.total} cancel{reasons.total !== 1 ? "s" : ""} in the last {reasons.days} days · one-question survey shown right before final confirm.
                                        </p>
                                        {reasons.total === 0 ? (
                                            <p className="mt-3 text-sm text-slate-500">
                                                No cancels with a reason in this window — either nobody is churning or nobody has picked a reason yet.
                                            </p>
                                        ) : (
                                            <div className="mt-3 space-y-1.5">
                                                {reasons.counts.map((row) => {
                                                    const pct = Math.round((row.count / reasons.total) * 100);
                                                    return (
                                                        <div
                                                            key={row.reason}
                                                            className="grid grid-cols-[1fr,auto] items-center gap-3"
                                                            data-testid={`cancel-reason-row-${row.reason}`}
                                                        >
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <div className="flex-1 h-5 rounded-full bg-slate-100 overflow-hidden min-w-[120px]">
                                                                    <div
                                                                        className="h-full rounded-full"
                                                                        style={{
                                                                            width: `${Math.max(3, pct)}%`,
                                                                            background: BRANCH_META.humanities.color,
                                                                        }}
                                                                    />
                                                                </div>
                                                                <span className="text-sm text-slate-800 whitespace-nowrap">
                                                                    {reasonLabel(row.reason)}
                                                                </span>
                                                            </div>
                                                            <span className="font-display font-black text-slate-900 text-sm">
                                                                {row.count}
                                                                <span className="text-slate-400 font-normal ml-1 text-xs">
                                                                    ({pct}%)
                                                                </span>
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {reasons.recent_details?.length > 0 && (
                                            <details className="mt-4" data-testid="cancel-reason-details">
                                                <summary className="text-xs font-display font-bold text-slate-500 cursor-pointer">
                                                    Free-text reasons ({reasons.recent_details.length})
                                                </summary>
                                                <div className="mt-2 space-y-2">
                                                    {reasons.recent_details.map((d, i) => (
                                                        <div key={i} className="rounded-xl bg-white/60 p-2 text-xs">
                                                            <div className="text-slate-500 font-mono">
                                                                {d.email} · {reasonLabel(d.reason)} ·{" "}
                                                                {new Date(d.cancelled_at).toLocaleDateString()}
                                                            </div>
                                                            <div className="text-slate-800 mt-0.5">{d.detail}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </details>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Digest preview modal — iframe renders the exact HTML that would ship. */}
            {previewOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4"
                    data-testid="digest-preview-modal"
                >
                    <div className="crystal rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200/70">
                            <div className="min-w-0">
                                <div className="font-display font-black text-xl tracking-tighter text-slate-900 truncate">
                                    {previewLoading ? "Rendering…" : previewData?.subject || "Digest preview"}
                                </div>
                                {previewData?.email && (
                                    <div className="text-xs text-slate-500 font-mono">
                                        as {previewData.email} · jobs: {previewData.counts?.jobs} · bounties: {previewData.counts?.bounties} · peers: {previewData.counts?.peers}
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => setPreviewOpen(false)}
                                className="text-slate-400 hover:text-slate-900"
                                data-testid="digest-preview-close"
                            >
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-2 bg-slate-100">
                            {previewError && (
                                <div className="text-sm text-white bg-sciences px-3 py-2 rounded-xl m-3" data-testid="digest-preview-error">
                                    {previewError}
                                </div>
                            )}
                            {previewLoading && (
                                <p className="text-slate-500 text-sm p-4">Building the digest…</p>
                            )}
                            {previewData?.html && (
                                <iframe
                                    title="Digest preview"
                                    srcDoc={previewData.html}
                                    className="w-full h-[70vh] rounded-xl bg-white border border-slate-200"
                                    data-testid="digest-preview-iframe"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function reasonLabel(key) {
    return (
        {
            too_expensive: "Too expensive",
            not_using: "Not using it enough",
            missing_features: "Missing features",
            found_alternative: "Found alternative",
            just_testing: "Just testing",
            other: "Other",
            unspecified: "Unspecified",
        }[key] || key
    );
}

function Stat({ label, value, rate, color, Icon }) {
    return (
        <div className="crystal rounded-2xl p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" color={color} />
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</div>
            </div>
            <div className="font-display font-black text-3xl tracking-tighter text-slate-900 mt-1">
                {value ?? 0}
            </div>
            {rate !== undefined && (
                <div className="text-[11px] text-slate-500 mt-0.5">{(rate * 100).toFixed(1)}% rate</div>
            )}
        </div>
    );
}

function Bar({ value, max, color, label }) {
    const pct = Math.max(2, Math.round((value / max) * 100));
    return (
        <div className="flex-1 flex items-center gap-1.5 min-w-0" title={`${label}: ${value}`}>
            <div className="flex-1 h-4 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color, minWidth: value > 0 ? 4 : 0 }} />
            </div>
            <span className="text-[11px] font-mono text-slate-600 w-8 text-right">{value}</span>
        </div>
    );
}
